import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, type OnDestroy, signal } from '@angular/core';
import { Client, type Message, type StompSubscription } from '@stomp/stompjs';
import { interval, map, type Observable, type Subscription, tap } from 'rxjs';
import SockJS from 'sockjs-client';

import { environment } from '../../../environments/environment';

import { type PagedUserNotification, type UserNotification } from './notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private http = inject(HttpClient);

  notifications: UserNotification[] = [];

  private baseUrl = `${environment.apiBaseUrl}/userNotification`;
  private readonly _unreadCount = signal(0);
  readonly unreadCount = this._unreadCount.asReadonly();

  getAllNotifications(page: number, size: number, sort?: string): Observable<PagedUserNotification> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (sort) {
      params = params.set('sort', sort);
    }
    return this.http.get<PagedUserNotification>(`${this.baseUrl}/find-all-paged`, { params }).pipe(tap(data => (this.notifications = data.content ?? [])));
  }

  markAsRead(id: string): Observable<void> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put<void>(`${this.baseUrl}/markAsRead/${id}`, {}, { headers });
  }

  markAsUnread(id: string): Observable<void> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put<void>(`${this.baseUrl}/markAsUnread/${id}`, {}, { headers });
  }

  archiveNotification(id: string): Observable<void> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete<void>(`${this.baseUrl}/archiveNotification/${id}`, { headers });
  }

  // ────────────────────────────────────────────────
  // 🔗 STOMP/WebSocket handling
  // ────────────────────────────────────────────────
  private client!: Client;
  private pollingSub?: Subscription;
  private watchdogSub?: Subscription;

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private reconnecting = false; // ✅ new guard flag

  // new helpers at top of class
  private reconnectLock: Promise<void> | null = null;
  private reconnectLockResolver: (() => void) | null = null;
  private notificationSubscription?: StompSubscription; // store the subscribe result if library returns it

  // ------------------------------
  // connect (minor guard additions)
  // ------------------------------
  connect(): void {
    if (!environment.useWebSocket) {
      this.startPolling();
      return;
    }

    // Ensure singleton service usage (also make this service providedIn: 'root')
    if (this.client && this.client.active && this.client.connected) {
      return;
    }

    // If a reconnect is already in progress, wait for it to finish and then return (prevents double-activate)
    if (this.reconnectLock) {
      // don't await here forever (fire-and-forget); but prevent immediate duplicate activate
      console.log('Connect requested but reconnect in progress; skipping immediate activate.');
      return;
    }

    this.client = new Client({
      webSocketFactory: () => new SockJS(environment.wsUrl),
      debug: str => console.log('[STOMP]', str),
      reconnectDelay: 0, // manual handling
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      beforeConnect: () => {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('Missing token');
        }
        this.client.connectHeaders = { Authorization: 'Bearer ' + token };
      },
      onStompError: frame => {
        console.error('Broker error:', frame.headers['message'], frame.body);
        // only kick off disconnect/reconnect if not already doing it
        this.handleDisconnection('STOMP error');
      },
      onWebSocketClose: evt => {
        console.warn('onWebSocketClose', evt);
        this.handleDisconnection('WebSocket closed');
      }
    });

    this.client.onConnect = frame => {
      console.log('✅ WebSocket connected:', frame);
      this.reconnectAttempts = 0;
      // don't flip reconnecting here — reconnectLock controls concurrency

      // cleanup fallback/polling BEFORE creating new subscription
      this.stopPolling();
      this.stopWatchdog();

      // unsubscribe any previous subscription reference (defensive)
      try {
        this.notificationSubscription?.unsubscribe?.();
      } catch {
        /* ignore */
      }
      this.notificationSubscription = this.client.subscribe('/user/queue/notifications', (message: Message) => {
        try {
          const count = JSON.parse(message.body);
          this._unreadCount.set(count);
        } catch (err) {
          console.error('Failed to parse notification message:', err);
        }
      });

      this.startWatchdog();
    };

    this.client.activate();
  }

  // ────────────────────────────────────────────────
  // 🩺 Connection Health Watchdog
  // ────────────────────────────────────────────────
  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdogSub = interval(15000).subscribe(async () => {
      const token = this.getAuthToken();
      if (!token) {
        this.disconnect();
        return;
      }

      if (this.client && this.client.active && !this.client.connected) {
        console.warn('⚠️ STOMP session appears dead while WebSocket active. Forcing reconnect...');
        await this.safeReconnect('Watchdog detected dead session');
      }
    });
  }

  private stopWatchdog(): void {
    this.watchdogSub?.unsubscribe();
    this.watchdogSub = undefined;
  }

  // ------------------------------
  // handleDisconnection + backoff
  // ------------------------------
  private async handleDisconnection(reason: string): Promise<void> {
    const token = this.getAuthToken();
    if (!token) {
      console.log(`${reason}: user logged out, skipping reconnect.`);
      return;
    }

    console.warn(`${reason}, starting fallback polling...`);
    if (!this.pollingSub) {
      this.startPolling();
    }

    // ensure reconnect attempts serialized
    await this.tryReconnectWithBackoff();
  }

  private async tryReconnectWithBackoff(): Promise<void> {
    // ensure only one caller gets to perform backoff & reconnect sequence
    await this.acquireReconnectLock();
    try {
      if (!this.getAuthToken()) {
        console.warn('No token, skipping reconnect.');
        return;
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.warn('Max reconnect attempts reached. Giving up on WebSocket.');
        return;
      }

      // increment and compute delay
      this.reconnectAttempts++;
      const delay = Math.min(this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
      console.log(`🔁 Reconnect attempt ${this.reconnectAttempts}, retrying in ${delay / 1000}s`);

      await new Promise(res => setTimeout(res, delay));

      // perform safe reconnect (serialized by the lock already)
      await this.safeReconnect('Backoff timer elapsed');
    } finally {
      this.releaseReconnectLock();
    }
  }

  // ------------------------------
  // safeReconnect (serialized by lock but also double-checked)
  // ------------------------------
  private async safeReconnect(reason: string): Promise<void> {
    if (!this.getAuthToken()) {
      console.warn('No token, skipping safe reconnect.');
      return;
    }

    // If already connected, no-op
    if (this.client && this.client.active && this.client.connected) {
      console.log('Already connected, safeReconnect no-op.');
      return;
    }

    console.warn(`${reason}: safely reconnecting...`);

    try {
      // stop watchdog/polling + unsubscribe before touching client
      this.stopWatchdog();
      this.stopPolling();

      try {
        // unsubscribe subscription reference if present
        try {
          this.notificationSubscription?.unsubscribe?.();
        } catch {
          /* ignore */
        }
        this.notificationSubscription = undefined;
      } catch {
        /* ignore */
      }

      // if client exists and active: deactivate, wait for completion
      if (this.client && this.client.active) {
        // stomp.js deactivate returns a Promise
        try {
          await this.client.deactivate();
        } catch (err) {
          console.warn('Error while deactivating client (ignored):', err);
        }
        // allow sockjs to tear down
        await new Promise(res => setTimeout(res, 800));
      }

      // create a fresh client if needed to avoid weird state carry-over
      // (optionally reuse existing client object if you prefer)
      this.connect();
    } catch (err) {
      console.error('Safe reconnect failed:', err);
    }
  }

  // ------------------------------
  // centralized reconnect lock
  // ------------------------------
  private async acquireReconnectLock(): Promise<void> {
    while (this.reconnectLock) {
      // wait for current lock to finish
      await this.reconnectLock;
    }
    // create a new lock promise and expose its resolver
    this.reconnectLock = new Promise<void>(resolve => {
      this.reconnectLockResolver = resolve;
    });
  }

  private releaseReconnectLock(): void {
    try {
      this.reconnectLockResolver?.();
    } finally {
      this.reconnectLock = null;
      this.reconnectLockResolver = null;
    }
  }

  // ────────────────────────────────────────────────
  // 🧠 Helper methods
  // ────────────────────────────────────────────────
  private getAuthToken(): string | null {
    const tokenWithBearer = localStorage.getItem('token')?.trim();
    return tokenWithBearer && tokenWithBearer.startsWith('Bearer ') ? tokenWithBearer.substring('Bearer '.length).trim() : null;
  }

  // ────────────────────────────────────────────────
  // 🕓 Polling fallback
  // ────────────────────────────────────────────────
  startPolling(): void {
    if (!this.getAuthToken()) {
      this.stopPolling();
      return;
    }

    this.stopPolling();
    this.pollingSub = interval(30000).subscribe(() => this.getUnreadCount().subscribe());
    this.getUnreadCount().subscribe();
  }

  stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  getUnreadCount(): Observable<number> {
    return this.http.get<{ messageCode: string }>(`${this.baseUrl}/get-unread-count-for-logged-in-user`).pipe(
      map(res => +res.messageCode),
      tap({
        next: count => this._unreadCount.set(count),
        error: err => {
          if (err.status === 401 || err.status === 403) {
            console.warn('Session expired, deactivating STOMP client.');
            this.disconnect();
          }
        }
      })
    );
  }

  // ------------------------------
  // disconnect (improve cleanup)
  // ------------------------------
  disconnect(): void {
    console.log('🛑 Disconnecting WebSocket client...');
    this.stopPolling();
    this.stopWatchdog();

    if (!environment.useWebSocket) {
      return;
    }

    try {
      this.notificationSubscription?.unsubscribe?.();
    } catch {
      /* ignore */
    }
    this.notificationSubscription = undefined;

    if (this.client && this.client.active) {
      // deactivate returns Promise; we can await if needed. Fire-and-forget here.
      this.client.deactivate().catch(err => console.warn('deactivate error', err));
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
